import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import SelectExceptionDateModal from "../components/Modal/SelectExceptionalDateModal";
import SelectOpeningHoursModal from "../components/Modal/SelectOpeningHoursModal";
import Navbar from "../components/Navbar";
import TourManagementOptionsSidebar from "../components/tour/TourManagementSidebarOptionsSidebar";
import TourManagementAvailabilityHeader from "../components/TourManagementAvailabilityHeader";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectExceptionalDateModal,
  setSelectOpeningHoursModal,
} from "../reducer/modalReducer";
import BasicDatePicker from "../components/input/DatePicker";
import { Paths } from "../router/Paths";
import useOnLoadPage from "../hooks/useOnLoadPage";
import { RootState } from "../app/store";
import { setOptionsData } from "../reducer/tourReducer";
import { useOptions } from "../hooks/useOptions";
import { resetOptionEdit } from "../reducer/tourEditReducer";
import { Icon } from "@iconify/react";

const weekData = [
  {
    value: "Monday",
    label: "Mon",
    start: "",
    end: "",
  },
  {
    value: "Tuesday",
    label: "Tue",
    start: "",
    end: "",
  },
  {
    value: "Wednesday",
    label: "Wed",
    start: "",
    end: "",
  },
  {
    value: "Thursday",
    label: "Thu",
    start: "",
    end: "",
  },
  {
    value: "Friday",
    label: "Fri",
    start: "",
    end: "",
  },
  {
    value: "Saturday",
    label: "Sat",
    start: "",
    end: "",
  },
  {
    value: "Sunday",
    label: "Sun",
    start: "",
    end: "",
  },
];

function formatDate(inputDate: any) {
  const date = new Date(inputDate);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function TourManagementPackageAvailabilitySchedulePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { optionsData, packageData } = useSelector(
    (state: RootState) => state.tour
  );
  const [error, setError] = useState("");
  const {
    tourEdit,
    optionId,
    id: editPackageId,
  } = useSelector((state: RootState) => state.edit);

  const { createPreOptions } = useOptions();
  const progressref = useRef(14);

  useEffect(() => {
    if (location.state && location.state.optionData) {
      dispatch(setOptionsData(location.state.optionData));
    }
  }, [location.state, dispatch]);

  const [weeklySchedule, setWeeklySchedule] = useState<
    { value?: string; label?: string; start?: string; end?: string }[]
  >(optionsData?.scheduleSettings?.weeklySchedule || weekData);
  const [exceptions, setExceptions] = useState<
    { date?: string; start?: string; end?: string }[]
  >(optionsData?.scheduleSettings?.exceptions || []);
  const [timeType, setTimeType] = useState("");
  const [timeIndex, setTimeIndex] = useState<number | null>(null);

  useOnLoadPage({ tabActive: "create" });

  const handleAddDate = () => {
    dispatch(setSelectExceptionalDateModal(true));
  };

  const handleSaveExceptionDate = (date: any) => {
    let data = [
      ...exceptions,
      {
        date: date,
        start: "",
        end: "",
      },
    ];
    setExceptions(data);
    dispatch(
      setOptionsData({
        ...optionsData,
        scheduleSettings: {
          ...optionsData?.scheduleSettings,
          exceptions: data,
        },
      })
    );
  };

  const hanldleRemoveException = (index: number) => {
    let exceptions1 = exceptions.filter((_, idx) => idx !== index);
    setExceptions(exceptions1);
    dispatch(
      setOptionsData({
        ...optionsData,
        scheduleSettings: {
          ...optionsData?.scheduleSettings,
          exceptions: exceptions1,
        },
      })
    );
  };

  const handleOpenTimeModal = (type: string, index: number) => {
    setTimeIndex(index);
    setTimeType(type);
    dispatch(setSelectOpeningHoursModal(true));
  };

  const handleSaveOpeningHour = (start: string, end: string) => {
    if (timeIndex === null) return;

    if (timeType === "day") {
      const days = weeklySchedule.map((schedule, index) => {
        if (index === timeIndex) {
          return {
            ...schedule,
            start,
            end,
            time: `${start} - ${end}`,
            day: schedule.value,
          };
        }

        return schedule;
      });

      setWeeklySchedule(days);
      dispatch(
        setOptionsData({
          ...optionsData,
          scheduleSettings: {
            ...optionsData?.scheduleSettings,
            weeklySchedule: days,
          },
        })
      );
    } else {
      const updatedExceptions = exceptions.map((exception, index) => {
        if (index === timeIndex) {
          return {
            ...exception,
            start,
            end,
            time: `${start} - ${end}`,
            day: exception.date,
          };
        }

        return exception;
      });

      setExceptions(updatedExceptions);
      dispatch(
        setOptionsData({
          ...optionsData,
          scheduleSettings: {
            ...optionsData?.scheduleSettings,
            exceptions: updatedExceptions,
          },
        })
      );
    }
  };

  useEffect(() => {
    if (optionsData.scheduleSettings?.hasEndDate === undefined) {
      dispatch(
        setOptionsData({
          ...optionsData,
          scheduleSettings: {
            ...optionsData.scheduleSettings,
            hasEndDate: false,
          },
        })
      );
    }
  }, [optionsData?.scheduleSettings?.hasEndDate, dispatch]);

  // const onContinue = () => {
  //   navigate(Paths.tourManagementPackageOptionsAvailabilityPriceCategory);
  // };

  const handleSave = async () => {
    const scheduleSettings = optionsData?.scheduleSettings || {};
    const { name, startingDate, weeklySchedule } = scheduleSettings;

    // --- Validation ---
    if (!name?.trim()) {
      setError("Please enter a name for your schedule.");
      return;
    }

    if (!startingDate) {
      setError("Please select a starting date for your activity.");
      return;
    }

    const hasValidSchedule =
      weeklySchedule && weeklySchedule.some((day) => day.start && day.end);

    if (!hasValidSchedule) {
      setError("Please add at least one schedule with start and end times.");
      return;
    }

    // clear error if validation passes
    setError("");

    // --- Proceed to Save ---
    const updatedPackageData = {
      ...optionsData,
      _id: optionsData._id,
      progress: Number(progressref.current),
    };

    dispatch(setOptionsData(updatedPackageData));

    const updatedPackageData2 = {
      ...optionsData,
      ...updatedPackageData,
    };

    try {
      if (tourEdit) {
        dispatch(resetOptionEdit());
        dispatch(setOptionsData(updatedPackageData2));
        const pkgId = packageData._id;
        navigate(`/manage/product/${pkgId}/${optionId}`, {
          state: {
            fromEdit: true,
            option: updatedPackageData2,
            packageId: pkgId,
          },
        });
        return;
      } else {
        await createPreOptions(updatedPackageData);
      }
      navigate(Paths.tourManagementPackageOptionsAvailabilityPriceCategory);
    } catch (error) {
      console.error("Error saving package:", error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleClearDayTime = (index: number) => {
    const updatedSchedule = weeklySchedule.map((day, idx) =>
      idx === index ? { ...day, start: "", end: "", time: "" } : day
    );

    setWeeklySchedule(updatedSchedule);

    // Update Redux store
    dispatch(
      setOptionsData({
        ...optionsData,
        scheduleSettings: {
          ...optionsData?.scheduleSettings,
          weeklySchedule: updatedSchedule,
        },
      })
    );
  };

  const handleCopyToRemainingDays = (index: number) => {
    const dayToCopy = weeklySchedule[index];
    const updatedSchedule = weeklySchedule.map((day, idx) =>
      idx > index
        ? {
            ...day,
            start: dayToCopy.start,
            end: dayToCopy.end,
            time: `${dayToCopy.start} - ${dayToCopy.end}`,
          }
        : day
    );

    setWeeklySchedule(updatedSchedule);

    dispatch(
      setOptionsData({
        ...optionsData,
        scheduleSettings: {
          ...optionsData?.scheduleSettings,
          weeklySchedule: updatedSchedule,
        },
      })
    );
  };

  console.log("package", optionsData);

  return (
    <div className={Classname.container}>
      <SelectExceptionDateModal onConfirm={handleSaveExceptionDate} />
      <SelectOpeningHoursModal onConfirm={handleSaveOpeningHour} />
      <Navbar />
      <div className={Classname.content}>
        <TourManagementOptionsSidebar selected={4} />
        <div className={Classname.detail}>
          <TourManagementAvailabilityHeader active={0} />
          <div className={Classname.title}>Name your schedule</div>
          <input
            type="text"
            value={optionsData?.scheduleSettings?.name}
            className={Classname.input}
            placeholder="E.g Summer Weekend’s prices"
            onChange={(e) => {
              dispatch(
                setOptionsData({
                  ...optionsData,
                  scheduleSettings: {
                    ...optionsData?.scheduleSettings,
                    name: e.target.value,
                  },
                })
              );
            }}
          />
          <div className={Classname.title}>
            What’s the starting date of your activity?
          </div>
          <BasicDatePicker
            lable={
              optionsData?.scheduleSettings?.startingDate
                ? new Date(optionsData.scheduleSettings.startingDate)
                : undefined
            }
            onSelect={(date) => {
              dispatch(
                setOptionsData({
                  ...optionsData,
                  scheduleSettings: {
                    ...optionsData?.scheduleSettings,
                    startingDate: date,
                  },
                })
              );
            }}
          />
          <FormGroup className="mt-2">
            <FormControlLabel
              control={
                <Checkbox
                  checked={optionsData?.scheduleSettings?.hasEndDate || false}
                  onChange={() => {
                    dispatch(
                      setOptionsData({
                        ...optionsData,
                        scheduleSettings: {
                          ...optionsData?.scheduleSettings,
                          hasEndDate: optionsData?.scheduleSettings?.hasEndDate
                            ? false
                            : true,
                        },
                      })
                    );
                  }}
                />
              }
              label="My activity has an end date"
            />
          </FormGroup>

          {optionsData?.scheduleSettings?.hasEndDate && (
            <BasicDatePicker
              lable={
                optionsData?.scheduleSettings?.endDate
                  ? new Date(optionsData.scheduleSettings.endDate)
                  : undefined
              }
              onSelect={(date) => {
                dispatch(
                  setOptionsData({
                    ...optionsData,
                    scheduleSettings: {
                      ...optionsData?.scheduleSettings,
                      endDate: date,
                    },
                  })
                );
              }}
            />
          )}

          {!optionsData?.operatingHours && (
            <>
              <span className="mt-8 mb-2">Standard weekly schedule</span>

              {weeklySchedule.map((schedule, index) => (
                <div
                  className={Classname.day}
                  key={index}
                  onClick={() => handleOpenTimeModal("day", index)}
                >
                  <span className={Classname.dayLabel}>{schedule.label}</span>
                  {schedule.start && schedule.end ? (
                    <div className={Classname.dayAddContainer}>
                      <span>{schedule.start}</span> -{" "}
                      <span>{schedule.end}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearDayTime(index);
                        }}
                      >
                        <Icon
                          icon="material-symbols:close"
                          className="text-red-500 size-6"
                        />
                      </button>
                      <button
                        className="underline text-primary1 ml-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyToRemainingDays(index);
                        }}
                      >
                        Copy to remaining days
                      </button>
                    </div>
                  ) : (
                    <div className={Classname.dayAddContainer}>
                      <img
                        src="/IconClock.svg"
                        alt=""
                        className={Classname.dayAddIcon}
                      />
                      <span>Add opening hours</span>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          <span className={Classname.title}>Exceptions (Optional)</span>

          <span className={Classname.title2}>
            Do you have alternative operating hours?
          </span>
          <span className={Classname.description}>
            Use this if you want different operating hours on a special day,
            like Easter or Christmas
          </span>

          {exceptions.map((date, index) => (
            <div className={Classname.day2} key={index}>
              <img
                src="/IconClose2.svg"
                alt=""
                className={Classname.exceptionCloseIcon}
                onClick={() => hanldleRemoveException(index)}
              />
              <div className={Classname.exceptionDate}>
                <img
                  src="/IconDate.svg"
                  alt=""
                  className={Classname.exceptionDateIcon}
                />
                <span>{formatDate(date?.date)}</span>
              </div>
              {date?.start && date?.end ? (
                <div
                  className={Classname.dayAddContainer}
                  onClick={() => handleOpenTimeModal("exception", index)}
                >
                  <span>{date.start}</span> - <span>{date.end}</span>
                </div>
              ) : (
                <div
                  className={Classname.dayAddContainer}
                  onClick={() => handleOpenTimeModal("exception", index)}
                >
                  <img
                    src="/IconClock.svg"
                    alt=""
                    className={Classname.dayAddIcon}
                  />
                  <span>Add opening hours</span>
                </div>
              )}
            </div>
          ))}

          {error && (
            <p className="text-red-500 mt-3 text-sm font-medium text-right">
              {error}
            </p>
          )}

          <div className="w-fit mt-4">
            <button className={Classname.saveButton} onClick={handleAddDate}>
              Add Date
            </button>
          </div>

          <div className={Classname.buttonsContainer}>
            <button className={Classname.saveButton} onClick={handleBack}>
              Back
            </button>
            <button className={Classname.continueButton} onClick={handleSave}>
              Save and {!tourEdit ? "Continue" : "Exit"}
            </button>
          </div>
        </div>
      </div>{" "}
    </div>
  );
}

export default TourManagementPackageAvailabilitySchedulePage;

const Classname = {
  container:
    "flex w-full min-h-screen relative bg-white flex-col items-center font-main",
  content:
    "flex flex-col lg:flex-row w-full max-w-7xl flex-1 pt-10 lg:pt-0 max-lg:px-4",
  detail: "flex flex-col py-10 lg:px-6 w-full",
  title: "font-bold text-lg mt-6 mb-2",
  title2: "font-bold lg:text-lg mt-6 mb-2",
  input: "flex w-full h-14 border border-gray-400 rounded-md px-4 mt-2",
  datePickerInput: "w-full border border-primary/20 rounded-md p-2 h-14 mt-2",
  textarea:
    "flex w-full h-44 py-4 border border-gray-400 rounded-md px-4 mt-2 mb-6 ",
  description: "flex text-base mb-4 mt-2",
  buttonsContainer: "flex flex-1 justify-between lg:justify-end gap-3 mt-10",
  addressButton:
    "font-bold w-fit flex items-center gap-2 cursor-pointer text-primary1 py-3 px-3  border rounded-lg",
  saveButton:
    "h-12 px-6 flex items-center justify-center font-bold text-primary1 border border-primary1 rounded-md",
  continueButton:
    "h-12 px-6 flex items-center justify-center text-white font-bold bg-primary1 rounded-md",

  day: "flex items-start lg:items-center gap-2 lg:gap-4 py-5 cursor-pointer border-b border-gray-400 w-full",
  day2: "flex items-center gap-2 lg:gap-4 py-5 cursor-pointer border-b border-gray-400 w-full max-lg:flex-wrap",
  dayLabel: "w-14 flex items-center justify-center text-center ",
  dayAddContainer: "flex text-primary1 items-center gap-3 w-full flex-wrap",
  dayAddIcon: "",

  exceptionCloseIcon: "",
  exceptionDate:
    "py-2 px-3 ml-3 lg:ml-6 mr-2 lg:mr-4 border border-gray-400 rounded-md flex items-center gap-2 text-sm flex-shrink-0",
  exceptionDateIcon: "",
};
